import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GloablButton from "./GloablButton";
import "../styles/ProductCatalog.css";
// import productDeskImage from "../assests/ProductImages/productPicture.svg";
import ProductCatalogIcon from "../icons/BusinessHub/ProductCatalogBlue";
import FilterIcon from "../icons/BusinessHub/FilterIcon";
import ExportAndPrintIcon from "../icons/BusinessHub/ExportAndPrintIcon";
import ShareIcon from "../icons/BusinessHub/ShareIcon";
import CubeIcon from "../icons/BusinessHub/CubeIcon";
import CloseIcon from "../icons/SignInIcon/closeIcon";
import SortIcon from "../icons/BusinessHub/SortIcon";
import OutOfStockIcon from "../icons/BusinessHub/OutOfStockIcon";
import Uploadbutton from "../icons/BusinessHub/Uploadbutton";
import ArrowDownIcon from "../icons/BusinessHub/ArrowDownIcon";
import ArrowBackIcon from "../icons/BusinessHub/ArrowBackIcon";
import ArrowFront from "../icons/BusinessHub/ArrowFront";
import ProductController from "../controllers/ProductController";
import BulkUpdateController from "../controllers/BulkUpDateController";
import { userService } from "../services/userService";
import Utils from "../utils/Utils";
import CustomDropdown from "./StatusDropdown";
import useTableNavigationState from "../utils/useTableNavigationState";

const productCatalogSummary = {
  title: "Product Catalog",
  description: "Browse validated products and variants.",
  totalProductsLabel: "products found",
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE_PAGE = 10;
const PRODUCT_CATALOG_DEFAULT_FILTERS = {
  search: "",
  category: [],
  brand: [],
  color: [],
  size: [],
  productCompanyStatus: [],
  productSystemStatus: [],
  minPrice: "",
  maxPrice: "",
};
const PRODUCT_CATALOG_DEFAULT_SORT = {
  price: "",
  name: "",
  rating: "",
  popularity: "",
};
const PRODUCT_CATALOG_TABLE_DEFAULTS = {
  page: DEFAULT_PAGE,
  size: DEFAULT_SIZE_PAGE,
  filters: PRODUCT_CATALOG_DEFAULT_FILTERS,
  sort: PRODUCT_CATALOG_DEFAULT_SORT,
};
const SORT_OPTION_MAP = {
  "Price (Low > High)": {
    sortBy: "price",
    sortDirection: "asc",
  },
  "Price (High > Low)": {
    sortBy: "price",
    sortDirection: "desc",
  },
  "A - Z": {
    sortBy: "productName",
    sortDirection: "asc",
  },
  "Z - A": {
    sortBy: "productName",
    sortDirection: "desc",
  },
};
const STATUS_FILTER_MAP = {
  Pending: 0,
  Approved: 1,
  Rejected: 2,
  Resubmit: 3,
};
const EXPORT_COLUMNS = [
  { key: "PRODUCT_ID", label: "Product ID" },
  { key: "PRODUCT_NAME", label: "Product Name" },
  { key: "SKU_ID", label: "SKU ID" },
  { key: "SKU_CODE", label: "SKU Code" },
  { key: "BARCODE", label: "Barcode" },
  { key: "SELLING_PRICE", label: "Selling Price" },
  { key: "COST_PRICE", label: "Cost Price" },
  { key: "STOCK_QUANTITY", label: "Stock Quantity" },
  { key: "SKU_COLOR", label: "SKU Color" },
  { key: "CURRENCY", label: "Currency" },
];
const REQUIRED_EXPORT_COLUMNS = ["PRODUCT_ID", "PRODUCT_NAME", "SKU_ID"];
const DEFAULT_EXPORT_COLUMNS = EXPORT_COLUMNS.map((column) => column.key);
const EXPORT_SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price", label: "Price" },
  { value: "productName", label: "Product Name" },
];

const normalizeStoredId = (value, ...keys) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    for (const key of keys) {
      const candidate = value?.[key];
      if (
        candidate !== null &&
        candidate !== undefined &&
        String(candidate).trim()
      ) {
        return String(candidate).trim();
      }
    }
  }
  return "";
};

const getJwtString = () => {
  const raw = userService.getUserData?.() ?? userService.getUser?.();
  if (!raw) return "";
  if (typeof raw === "string") return raw;
  return raw?.jwt ?? raw?.token ?? "";
};

const mapSelectedFilterValues = (values, valueMap = {}) => {
  if (!Array.isArray(values) || values.length === 0) return "";

  return values
    .map((value) =>
      Object.prototype.hasOwnProperty.call(valueMap, value)
        ? valueMap[value]
        : value,
    )
    .filter((value) => value !== undefined && value !== null && value !== "")
    .join(",");
};

const cleanNumericFilterValue = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return "";
  const numericValue = Number(normalized);
  return Number.isFinite(numericValue) ? normalized : "";
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const normalizeCatalogFilters = (filters = {}) => ({
  search: filters.search || "",
  category: asArray(filters.category),
  brand: asArray(filters.brand),
  color: asArray(filters.color),
  size: asArray(filters.size),
  productCompanyStatus: asArray(filters.productCompanyStatus),
  productSystemStatus: asArray(filters.productSystemStatus),
  minPrice: filters.minPrice || "",
  maxPrice: filters.maxPrice || "",
});

const normalizeCatalogSort = (sort = {}) => ({
  price: sort.price || "",
  name: sort.name || "",
  rating: sort.rating || "",
  popularity: sort.popularity || "",
});

const getApiErrorMessage = (err, fallback) => {
  const payload = err?.response?.data ?? err?.data;
  const status = err?.response?.status ?? err?.status;
  if (status === 404) {
    return (
      payload?.message ||
      payload?.error ||
      "Resource not found (404). Verify companyID and try again."
    );
  }
  if (typeof payload === "string" && payload.trim()) return payload;
  if (Array.isArray(payload?.errors) && payload.errors.length) {
    return payload.errors.join(", ");
  }
  return payload?.message || payload?.error || err?.message || fallback;
};

const getStatusMeta = (rawStatus) => {
  const normalizedStatus = String(rawStatus || "")
    .trim()
    .toUpperCase();

  if (
    normalizedStatus.includes("ARCH") ||
    normalizedStatus.includes("REJECT") ||
    normalizedStatus.includes("INACTIVE")
  ) {
    return {
      badgeLabel: "Archived",
      badgeTone: "danger",
    };
  }

  if (normalizedStatus.includes("PEND") || normalizedStatus.includes("DRAFT")) {
    return {
      badgeLabel: "Pending",
      badgeTone: "warning",
    };
  }

  return {
    badgeLabel: "Approved",
    badgeTone: "success",
  };
};

const formatPrice = (value, currency = "USD") => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value || "N/A";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(numericValue);
  } catch (error) {
    return `$${numericValue.toFixed(2)}`;
  }
};

const normalizeCatalogItem = (item, index) => {
  const productID =
    item?.productID ??
    item?.productId ??
    item?.id ??
    item?.catalogId ??
    item?.productCatalogId ??
    `product-${index + 1}`;
  const productName =
    item?.productName ??
    item?.name ??
    item?.title ??
    item?.productTitle ??
    "Unnamed Product";
  const skuValue =
    item?.skuCode ?? item?.sku ?? item?.SKU ?? item?.productSKU ?? item?.code;
  const category =
    item?.categoryName ??
    item?.category ??
    item?.productCategory ??
    item?.subCategory ??
    "Uncategorized";
  const variantCount = Number(
    item?.variantCount ?? item?.variantsCount ?? item?.numberOfVariants ?? 0,
  );
  const stockQuantity =
    item?.stockQuantity ??
    item?.quantity ??
    item?.availableStock ??
    item?.inventoryCount ??
    0;
  const inStock = Number(stockQuantity) > 0;
  const { badgeLabel, badgeTone } = getStatusMeta(item?.status, stockQuantity);
  const currency = item?.currency || item?.priceCurrency || "USD";
  const priceValue =
    item?.price ?? item?.unitPrice ?? item?.sellingPrice ?? item?.amount;

  return {
    id: item?.skuID ?? item?.id ?? item?.productCatalogId ?? productID,
    productID,
    productName,
    sku: skuValue ? `SKU: ${skuValue}` : "SKU: N/A",
    category,
    price: formatPrice(priceValue, currency),
    variants: `${variantCount} variant${variantCount === 1 ? "" : "s"}`,
    stockStatus: inStock ? `${stockQuantity} in stock` : "Out of stock",
    stockTone: inStock ? "success" : "danger",
    badgeLabel,
    badgeTone,
    imageSrc: item?.images?.[0] || item?.imageUrl || item?.productImage || "",
    raw: item,
  };
};

const assertControllerSuccess = (response, fallbackMessage) => {
  const status = response?.status ?? response?.response?.status;
  if (status && status >= 200 && status < 300) return;

  const backendMessage =
    response?.data?.message ||
    response?.data?.error ||
    response?.response?.data?.message ||
    response?.response?.data?.error ||
    fallbackMessage;

  throw new Error(backendMessage);
};

const getExportFileName = (response) => {
  const disposition = response?.headers?.["content-disposition"];
  const fallbackName = `product-catalog-export-${new Date()
    .toISOString()
    .slice(0, 10)}.xlsx`;

  if (!disposition) return fallbackName;

  const utfFileName = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfFileName?.[1]) return decodeURIComponent(utfFileName[1]);

  const fileName = disposition.match(/filename="?([^"]+)"?/i);
  return fileName?.[1] || fallbackName;
};

const downloadExportFile = (response) => {
  const blob =
    response?.data instanceof Blob
      ? response.data
      : new Blob([response?.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = getExportFileName(response);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

const getExportErrorMessage = async (
  error,
  fallback = "Failed to export product catalog.",
) => {
  const data = error?.response?.data;

  if (data instanceof Blob) {
    const text = await data.text();
    if (text.trim()) {
      try {
        const parsed = JSON.parse(text);
        return parsed?.message || parsed?.error || text;
      } catch (e) {
        return text;
      }
    }
  }

  return getApiErrorMessage(error, fallback);
};

function ProductCatalog() {
  const { tableState, navigateWithTableState } = useTableNavigationState({
    defaults: PRODUCT_CATALOG_TABLE_DEFAULTS,
  });
  const [isTableStateInitialized, setIsTableStateInitialized] = useState(false);
  const jwt = getJwtString();
  const parsedJwt = (() => {
    try {
      return jwt ? Utils.parseJwt(jwt) : null;
    } catch (e) {
      return null;
    }
  })();
  const companyIDFromJwt = parsedJwt?.userDetails
    ? Object.keys(parsedJwt.userDetails)[0]
    : null;
  const storedComp = userService.getCompID?.();
  const companyID = userService.getCompID();

  const [activeModalKey, setActiveModalKey] = useState("");
  const sortPanelRef = useRef(null);
  const sortButtonRef = useRef(null);
  const filterPanelRef = useRef(null);
  const filterButtonRef = useRef(null);
  const importInputRef = useRef(null);
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [pageInputValue, setPageInputValue] = useState("");
  const [isPageInputFocused, setIsPageInputFocused] = useState(false);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(0);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedSortOptions, setSelectedSortOptions] = useState({
    price: "",
    name: "",
    rating: "",
    popularity: "",
  });
  const [filterSearch, setFilterSearch] = useState("");
  const [showAllFilterOptions, setShowAllFilterOptions] = useState({
    category: false,
    brand: false,
    color: false,
    size: false,
    productCompanyStatus: false,
    productSystemStatus: false,
  });
  const [selectedFilterOptions, setSelectedFilterOptions] = useState({
    category: [],
    brand: [],
    color: [],
    size: [],
    productCompanyStatus: [],
    productSystemStatus: [],
  });
  const [appliedFilterOptions, setAppliedFilterOptions] = useState({
    search: "",
    category: [],
    brand: [],
    color: [],
    size: [],
    productCompanyStatus: [],
    productSystemStatus: [],
    minPrice: "",
    maxPrice: "",
  });
  const [priceFilterValues, setPriceFilterValues] = useState({
    minPrice: "",
    maxPrice: "",
  });
  const [appliedSortOptions, setAppliedSortOptions] = useState({
    price: "",
    name: "",
    rating: "",
    popularity: "",
  });
  const currentTableState = useMemo(
    () => ({
      page: currentPage,
      size: DEFAULT_SIZE_PAGE,
      filters: appliedFilterOptions,
      sort: appliedSortOptions,
    }),
    [appliedFilterOptions, appliedSortOptions, currentPage],
  );

  const createProductDetailsNavigation = useCallback(
    (productCard, options = {}) => {
      const productId =
        productCard.raw?.productId ||
        productCard.raw?.productID ||
        productCard.productID;
      const navigationData = {
        returnPath: "/product-catalog",
        returnTableState: currentTableState,
        sourcePage: "product-catalog",
        ...(options.isEditing ? { isEditing: true } : {}),
        productID: productCard.productID,
        productId,
        companyId:
          productCard.raw?.companyId ||
          productCard.raw?.companyID ||
          companyID,
        businessProfileID:
          productCard.raw?.businessProfileID ||
          productCard.raw?.busienssProfileID ||
          userService.getBPID?.() ||
          "",
      };
      const path = `/product-catalog/details?data=${encodeURIComponent(
        Utils.AESEncrypt(JSON.stringify(navigationData)),
      )}`;

      return {
        path,
        state: navigationData,
      };
    },
    [companyID, currentTableState],
  );
  const [selectedExportColumns, setSelectedExportColumns] = useState(
    DEFAULT_EXPORT_COLUMNS,
  );
  const [exportSearch, setExportSearch] = useState("");
  const [exportSortBy, setExportSortBy] = useState("newest");
  const [exportSortDirection, setExportSortDirection] = useState("DESC");
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState("");
  const activeModalCopy = useMemo(() => {
    const modalMap = {
      sort: {
        title: "Sort Products",
        description: "Choose how the catalog should be ordered.",
        items: ["Newest first", "Price: Low to high", "Price: High to low"],
      },
      filter: {
        title: "Filter Products",
        description: "Filter by category, stock state, or approval status.",
        items: ["Furniture", "Electronics", "Approved only"],
      },
      export: {
        title: "Export Products",
        description: "Choose the Excel columns you want to include.",
        items: ["Export as Excel"],
      },
    };

    return modalMap[activeModalKey] || null;
  }, [activeModalKey]);
  const sortSections = [
    {
      key: "price",
      title: "Price Range",
      options: ["Price (Low > High)", "Price (High > Low)"],
    },
    {
      key: "name",
      title: "Name",
      options: ["A - Z", "Z - A"],
    },
    {
      key: "rating",
      title: "Rating",
      options: ["Highest Rated", "Lowest Rated"],
    },
    {
      key: "popularity",
      title: "Popularity",
      options: ["Most Popular"],
    },
  ];

  const filterSections = [
    {
      key: "productCompanyStatus",
      title: "Status",
      options: ["Pending", "Approved", "Rejected", "Resubmit"],
      singleSelect: true,
    },
    {
      key: "category",
      title: "Category",
      options: categoryOptions.map((category) => ({
        label: category.categoryName,
        value: category.categoryId,
      })),
    },
    {
      key: "brand",
      title: "Brand",
      options: ["Brand A", "Brand B", "Brand C"],
    },
    {
      key: "color",
      title: "Color",
      options: ["Brown", "Red", "Blue"],
    },
    {
      key: "size",
      title: "Size",
      options: ["XS", "Small"],
    },
  ];

  const clearSortSelections = () => {
    setSelectedSortOptions({
      price: "",
      name: "",
      rating: "",
      popularity: "",
    });
    setAppliedSortOptions({
      price: "",
      name: "",
      rating: "",
      popularity: "",
    });
    setCurrentPage(DEFAULT_PAGE);
  };

  const clearAllFilterSelections = () => {
    setFilterSearch("");
    setSelectedFilterOptions({
      category: [],
      brand: [],
      color: [],
      size: [],
      productCompanyStatus: [],
      productSystemStatus: [],
    });
    setPriceFilterValues({
      minPrice: "",
      maxPrice: "",
    });
    setShowAllFilterOptions({
      category: false,
      brand: false,
      color: false,
      size: false,
      productCompanyStatus: false,
      productSystemStatus: false,
    });
    setAppliedFilterOptions({
      search: "",
      category: [],
      brand: [],
      color: [],
      size: [],
      productCompanyStatus: [],
      productSystemStatus: [],
      minPrice: "",
      maxPrice: "",
    });
    setCurrentPage(DEFAULT_PAGE);
  };

  useEffect(() => {
    const getAllCategories = async () => {
      try {
        const response = await ProductController.getAllCategories({
          Authorization: userService.getUser(),
        });
        const categories = Array.isArray(response?.data) ? response.data : [];
        setCategoryOptions(
          categories.filter(
            (category) => category?.categoryId && category?.categoryName,
          ),
        );
      } catch (error) {
        setCategoryOptions([]);
      }
    };

    getAllCategories();
  }, []);

  useEffect(() => {
    if (activeModalKey !== "sort" && activeModalKey !== "filter") {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (activeModalKey === "sort") {
        if (sortPanelRef.current?.contains(event.target)) return;
        if (sortButtonRef.current?.contains(event.target)) return;
      }
      if (activeModalKey === "filter") {
        if (filterPanelRef.current?.contains(event.target)) return;
        if (filterButtonRef.current?.contains(event.target)) return;
      }
      setActiveModalKey("");
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [activeModalKey]);

  useEffect(() => {
    if (isTableStateInitialized) return;

    const restoredFilters = normalizeCatalogFilters(tableState.filters);
    const restoredSort = normalizeCatalogSort(tableState.sort);

    setCurrentPage(tableState.page || DEFAULT_PAGE);
    setAppliedFilterOptions(restoredFilters);
    setSelectedFilterOptions({
      category: restoredFilters.category,
      brand: restoredFilters.brand,
      color: restoredFilters.color,
      size: restoredFilters.size,
      productCompanyStatus: restoredFilters.productCompanyStatus,
      productSystemStatus: restoredFilters.productSystemStatus,
    });
    setFilterSearch(restoredFilters.search);
    setPriceFilterValues({
      minPrice: restoredFilters.minPrice,
      maxPrice: restoredFilters.maxPrice,
    });
    setAppliedSortOptions(restoredSort);
    setSelectedSortOptions(restoredSort);
    setIsTableStateInitialized(true);
  }, [isTableStateInitialized, tableState]);

  useEffect(() => {
    if (!isTableStateInitialized) return;

    navigateWithTableState("/product-catalog", currentTableState, {
      replace: true,
    });
  }, [currentTableState, isTableStateInitialized, navigateWithTableState]);

  useEffect(() => {
    const fetchProductCatalog = async () => {
      if (!isTableStateInitialized) return;

      if (!jwt) {
        setCatalogItems([]);
        setCatalogError("Missing authentication token.");
        setServerTotal(0);
        setServerTotalPages(0);
        return;
      }

      const activeCategory = mapSelectedFilterValues(
        appliedFilterOptions.category,
      );
      const activeBrand = mapSelectedFilterValues(appliedFilterOptions.brand);
      const activeColor = mapSelectedFilterValues(appliedFilterOptions.color);
      const activeSize = mapSelectedFilterValues(appliedFilterOptions.size);
      const activeCompanyStatus = mapSelectedFilterValues(
        appliedFilterOptions.productCompanyStatus,
        STATUS_FILTER_MAP,
      );
      const activeSystemStatus = mapSelectedFilterValues(
        appliedFilterOptions.productSystemStatus,
        STATUS_FILTER_MAP,
      );
      const activeMinPrice = cleanNumericFilterValue(
        appliedFilterOptions.minPrice,
      );
      const activeMaxPrice = cleanNumericFilterValue(
        appliedFilterOptions.maxPrice,
      );
      const activeSort =
        appliedSortOptions.price ||
        appliedSortOptions.name ||
        appliedSortOptions.rating ||
        appliedSortOptions.popularity ||
        "";
      const sortPayload = SORT_OPTION_MAP[activeSort] || {};
      const productRequest = {
        companyID,
        page: currentPage,
        sizePage: DEFAULT_SIZE_PAGE,
        ...(appliedFilterOptions.search
          ? { search: appliedFilterOptions.search }
          : {}),
        ...(activeBrand ? { brand: activeBrand } : {}),
        ...(activeColor ? { color: activeColor } : {}),
        ...(activeSize ? { size: activeSize } : {}),
        ...(activeCategory ? { categoryId: activeCategory } : {}),
        ...(activeCompanyStatus !== ""
          ? { productCompanyStatus: activeCompanyStatus }
          : {}),
        ...(activeSystemStatus !== ""
          ? { productSystemStatus: activeSystemStatus }
          : {}),
        ...(activeMinPrice ? { minPrice: activeMinPrice } : {}),
        ...(activeMaxPrice ? { maxPrice: activeMaxPrice } : {}),
        ...sortPayload,
      };

      try {
        setCatalogLoading(true);
        setCatalogError("");

        const response = await ProductController.getProductCatalog({
          Authorization: userService.getUser(),
          ...productRequest,
        });
        console.log("productRequest", productRequest);
        console.log("response", response);
        assertControllerSuccess(response, "Failed to load product catalog.");
        const data = response?.data ?? response?.response?.data;
        if (!data) {
          throw new Error("Failed to load product catalog.");
        }

        const rawList = Array.isArray(data?.rows) ? data.rows : [];
        const normalizedItems = rawList.map(normalizeCatalogItem);

        setCatalogItems(normalizedItems);
        setServerTotal(Number(data?.total ?? normalizedItems.length ?? 0));
        setServerTotalPages(Number(data?.totalPages ?? 0));
      } catch (error) {
        setCatalogItems([]);
        setServerTotal(0);
        setServerTotalPages(0);
        setCatalogError(
          getApiErrorMessage(error, "Failed to load product catalog."),
        );
      } finally {
        setCatalogLoading(false);
      }
    };

    fetchProductCatalog();
  }, [
    appliedFilterOptions,
    appliedSortOptions,
    companyID,
    currentPage,
    jwt,
    isTableStateInitialized,
  ]);

  useEffect(() => {
    if (!serverTotalPages || currentPage <= serverTotalPages) return;
    setCurrentPage(serverTotalPages || DEFAULT_PAGE);
  }, [currentPage, serverTotalPages]);

  useEffect(() => {
    if (activeModalKey === "export") {
      setExportSearch(appliedFilterOptions.search || "");
    }
  }, [activeModalKey, appliedFilterOptions.search]);

  const totalProductsValue = String(serverTotal || 0);

  const applySelectedFilters = () => {
    setAppliedFilterOptions({
      search: filterSearch.trim(),
      category: selectedFilterOptions.category,
      brand: selectedFilterOptions.brand,
      color: selectedFilterOptions.color,
      size: selectedFilterOptions.size,
      productCompanyStatus: selectedFilterOptions.productCompanyStatus,
      productSystemStatus: selectedFilterOptions.productSystemStatus,
      minPrice: priceFilterValues.minPrice.trim(),
      maxPrice: priceFilterValues.maxPrice.trim(),
    });
    setCurrentPage(DEFAULT_PAGE);
    setActiveModalKey("");
  };

  const applySelectedSort = () => {
    setAppliedSortOptions(selectedSortOptions);
    setCurrentPage(DEFAULT_PAGE);
    setActiveModalKey("");
  };

  const toggleExportColumn = (columnKey) => {
    if (REQUIRED_EXPORT_COLUMNS.includes(columnKey)) return;

    setExportError("");
    setSelectedExportColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((column) => column !== columnKey)
        : Array.from(new Set([...REQUIRED_EXPORT_COLUMNS, ...prev, columnKey])),
    );
  };

  const handleExportCatalog = async () => {
    if (!jwt) {
      setExportError("Missing authentication token.");
      return;
    }
    if (!companyID) {
      setExportError("Missing company ID.");
      return;
    }
    if (selectedExportColumns.length === 0) {
      setExportError("Select at least one column.");
      return;
    }

    if (!exportSortBy || !exportSortDirection) {
      setExportError("Choose sort by and sort direction.");
      return;
    }

    const request = {
      companyId: companyID,
      columns: Array.from(
        new Set([...REQUIRED_EXPORT_COLUMNS, ...selectedExportColumns]),
      ),
      search: exportSearch.trim(),
      sortBy: exportSortBy,
      sortDirection: exportSortDirection,
    };

    try {
      setExportLoading(true);
      setExportError("");
      const response = await BulkUpdateController.export({
        Authorization: userService.getUser(),
        request,
      });

      assertControllerSuccess(response, "Failed to export product catalog.");
      downloadExportFile(response);
      setActiveModalKey("");
    } catch (error) {
      setExportError(await getExportErrorMessage(error));
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = "";

    if (!selectedFile) return;
    if (!jwt) {
      setImportError("Missing authentication token.");
      return;
    }
    if (!companyID) {
      setImportError("Missing company ID.");
      return;
    }

    try {
      setImportLoading(true);
      setImportError("");
      const response = await BulkUpdateController.import({
        Authorization: userService.getUser(),
        companyId: companyID,
        file: selectedFile,
        validateOnly: false,
      });

      assertControllerSuccess(response, "Failed to import product catalog.");
      setAppliedFilterOptions((prev) => ({ ...prev }));
      setCurrentPage(DEFAULT_PAGE);
    } catch (error) {
      setImportError(
        await getExportErrorMessage(error, "Failed to import product catalog."),
      );
    } finally {
      setImportLoading(false);
    }
  };

  const canGoToPreviousPage = currentPage > DEFAULT_PAGE;
  const canGoToNextPage =
    serverTotalPages > 0 ? currentPage < serverTotalPages : false;
  const goToTypedPage = (value = pageInputValue) => {
    const trimmedPageInput = String(value).trim();
    if (!trimmedPageInput) return false;

    const requestedPage = Number(trimmedPageInput);

    if (!Number.isInteger(requestedPage)) return false;

    const safePage = Math.min(
      Math.max(requestedPage, DEFAULT_PAGE),
      serverTotalPages || DEFAULT_PAGE,
    );
    setCurrentPage(safePage);
    setPageInputValue("");
    setIsPageInputFocused(false);
    return true;
  };
  const getCatalogPages = () => {
    const totalPages = Number(serverTotalPages || 0);
    if (totalPages <= 0) return [];

    const pages = [1];
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 2) {
      start = 2;
      end = Math.min(3, totalPages - 1);
    }

    if (currentPage >= totalPages - 1) {
      start = Math.max(2, totalPages - 2);
      end = totalPages - 1;
    }

    if (start > 2) pages.push("ellipsis-start");

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (end < totalPages - 1) pages.push("ellipsis-end");
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  return (
    <div className="productCatalog-main-container">
      <div className="productCatalog-header-section">
        <div className="productCatalog-header-copy-block">
          <div className="productCatalog-header-icon-slot">
            <ProductCatalogIcon />
          </div>
          <div className="productCatalog-header-text-block">
            <div className="productCatalog-header-title">
              {productCatalogSummary.title}
            </div>
            <div className="productCatalog-header-description">
              {productCatalogSummary.description}
            </div>
          </div>
        </div>
      </div>

      <div className="productCatalog-toolbar-section">
        <div className="productCatalog-toolbar-result-count">
          <span className="productCatalog-toolbar-result-value">
            {totalProductsValue}
          </span>
          {productCatalogSummary.totalProductsLabel}
        </div>

        <div className="productCatalog-toolbar-actions-row">
          <div className="sort-wrapper">
            <GloablButton
              className="productCatalog-toolbar-action-button productCatalog-toolbar-action-button-sort"
              ref={sortButtonRef}
              width="108px"
              height="40px"
              backgroundColor="#FFFFFF"
              text="Sort"
              color="#424242"
              icon={<SortIcon />}
              gap="10px"
              padding="10px 15px"
              borderRadius="20px"
              border="1px solid #D0D5DD"
              boxShadow="none"
              onClick={() => setActiveModalKey("sort")}
            />
            {activeModalKey === "sort" ? (
              <div
                className="productCatalog-sort-panel"
                ref={sortPanelRef}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="productCatalog-sort-panel-title">Sort</div>

                {sortSections.map((section) => (
                  <div
                    key={section.key}
                    className="productCatalog-sort-section"
                  >
                    <div className="productCatalog-sort-section-head">
                      <div className="productCatalog-sort-section-title">
                        {section.title}
                      </div>
                      <button
                        type="button"
                        className="productCatalog-sort-clear-button"
                        onClick={() =>
                          setSelectedSortOptions((prev) => ({
                            ...prev,
                            [section.key]: "",
                          }))
                        }
                      >
                        Clear
                      </button>
                    </div>

                    <div className="productCatalog-sort-option-list">
                      {section.options.map((option) => (
                        <label
                          key={option}
                          className="productCatalog-sort-option-item"
                        >
                          <input
                            type="checkbox"
                            checked={
                              selectedSortOptions[section.key] === option
                            }
                            onChange={() =>
                              setSelectedSortOptions((prev) => ({
                                ...prev,
                                [section.key]:
                                  prev[section.key] === option ? "" : option,
                              }))
                            }
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="productCatalog-sort-footer">
                  <GloablButton
                    width="110px"
                    height="36px"
                    backgroundColor="#FFFFFF"
                    text="Clear All"
                    color="#667085"
                    fontWeight={600}
                    fontSize="12px"
                    lineHeight="16px"
                    border="2px solid #667085"
                    borderRadius="50px"
                    padding="10px 15px"
                    onClick={clearSortSelections}
                  />
                  <GloablButton
                    width="103px"
                    height="32px"
                    backgroundColor="#4072C8"
                    text="Apply All"
                    color="#FFFFFF"
                    fontWeight={600}
                    fontSize="12px"
                    lineHeight="16px"
                    borderRadius="50px"
                    padding="8px 12px"
                    boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.45)"
                    onClick={applySelectedSort}
                  />
                </div>
              </div>
            ) : null}
          </div>
          <div className="sort-wrapper">
            <GloablButton
              className="productCatalog-toolbar-action-button"
              ref={filterButtonRef}
              width="108px"
              height="40px"
              backgroundColor="#FFFFFF"
              text="Filter"
              color="#424242"
              icon={<FilterIcon />}
              gap="10px"
              padding="10px 15px"
              borderRadius="20px"
              border="1px solid #D0D5DD"
              boxShadow="none"
              onClick={() => setActiveModalKey("filter")}
            />
            {activeModalKey === "filter" ? (
              <div
                className="productCatalog-filter-panel"
                ref={filterPanelRef}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="productCatalog-filter-panel-title">Filter</div>

                <div className="productCatalog-filter-section">
                  <div className="productCatalog-filter-section-head">
                    <div className="productCatalog-filter-section-title">
                      Search
                    </div>
                    <button
                      type="button"
                      className="productCatalog-filter-clear-button"
                      onClick={() => setFilterSearch("")}
                    >
                      Clear
                    </button>
                  </div>

                  <div className="productCatalog-filter-search-shell">
                    <input
                      className="productCatalog-filter-search-input"
                      value={filterSearch}
                      onChange={(event) => setFilterSearch(event.target.value)}
                      placeholder="Search by product name, SKU, Category..."
                    />
                  </div>
                </div>

                <div className="productCatalog-filter-section">
                  <div className="productCatalog-filter-section-head">
                    <div className="productCatalog-filter-section-title">
                      Price
                    </div>
                    <button
                      type="button"
                      className="productCatalog-filter-clear-button"
                      onClick={() =>
                        setPriceFilterValues({
                          minPrice: "",
                          maxPrice: "",
                        })
                      }
                    >
                      Clear
                    </button>
                  </div>

                  <div className="productCatalog-filter-price-row">
                    <input
                      className="productCatalog-filter-search-input productCatalog-filter-price-input"
                      value={priceFilterValues.minPrice}
                      inputMode="decimal"
                      onChange={(event) =>
                        setPriceFilterValues((prev) => ({
                          ...prev,
                          minPrice: event.target.value,
                        }))
                      }
                      placeholder="Min"
                    />
                    <input
                      className="productCatalog-filter-search-input productCatalog-filter-price-input"
                      value={priceFilterValues.maxPrice}
                      inputMode="decimal"
                      onChange={(event) =>
                        setPriceFilterValues((prev) => ({
                          ...prev,
                          maxPrice: event.target.value,
                        }))
                      }
                      placeholder="Max"
                    />
                  </div>
                </div>

                {filterSections.map((section) => {
                  const visibleOptions = showAllFilterOptions[section.key]
                    ? section.options
                    : section.options.slice(0, 3);

                  return (
                    <div
                      key={section.key}
                      className="productCatalog-filter-section"
                    >
                      <div className="productCatalog-filter-section-head">
                        <div className="productCatalog-filter-section-title">
                          {section.title}
                        </div>
                        <button
                          type="button"
                          className="productCatalog-filter-clear-button"
                          onClick={() =>
                            setSelectedFilterOptions((prev) => ({
                              ...prev,
                              [section.key]: [],
                            }))
                          }
                        >
                          Clear
                        </button>
                      </div>

                      <div className="productCatalog-filter-option-list">
                        {visibleOptions.map((option) => {
                          const optionValue =
                            typeof option === "object" ? option.value : option;
                          const optionLabel =
                            typeof option === "object" ? option.label : option;
                          const isChecked =
                            selectedFilterOptions[section.key].includes(
                              optionValue,
                            );

                          return (
                            <label
                              key={optionValue}
                              className="productCatalog-filter-option-item"
                            >
                              <input
                                type={
                                  section.singleSelect ? "radio" : "checkbox"
                                }
                                name={
                                  section.singleSelect
                                    ? `productCatalog-${section.key}`
                                    : undefined
                                }
                                checked={isChecked}
                                onChange={() =>
                                  setSelectedFilterOptions((prev) => ({
                                    ...prev,
                                    [section.key]: section.singleSelect
                                      ? isChecked
                                        ? []
                                        : [optionValue]
                                      : isChecked
                                        ? prev[section.key].filter(
                                            (item) => item !== optionValue,
                                          )
                                        : [...prev[section.key], optionValue],
                                  }))
                                }
                              />
                              <span>{optionLabel}</span>
                            </label>
                          );
                        })}
                      </div>

                      {section.options.length > 3 ? (
                        <button
                          type="button"
                          className="productCatalog-filter-show-more-button"
                          onClick={() =>
                            setShowAllFilterOptions((prev) => ({
                              ...prev,
                              [section.key]: !prev[section.key],
                            }))
                          }
                        >
                          {showAllFilterOptions[section.key]
                            ? "Show less"
                            : "Show more"}
                        </button>
                      ) : null}
                    </div>
                  );
                })}

                <div className="productCatalog-filter-footer">
                  <GloablButton
                    width="110px"
                    height="36px"
                    backgroundColor="#FFFFFF"
                    text="Clear All"
                    color="#667085"
                    fontWeight={600}
                    fontSize="12px"
                    lineHeight="16px"
                    border="2px solid #667085"
                    borderRadius="50px"
                    padding="10px 15px"
                    onClick={clearAllFilterSelections}
                  />
                  <GloablButton
                    width="103px"
                    height="32px"
                    backgroundColor="#4072C8"
                    text="Apply All"
                    color="#FFFFFF"
                    fontWeight={600}
                    fontSize="12px"
                    lineHeight="16px"
                    borderRadius="50px"
                    padding="8px 12px"
                    boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.45)"
                    onClick={applySelectedFilters}
                  />
                </div>
              </div>
            ) : null}
          </div>
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="productCatalog-import-file-input"
            onChange={handleImportFileChange}
          />
          <GloablButton
            className="productCatalog-toolbar-action-button"
            width="118px"
            height="40px"
            backgroundColor={importLoading ? "#F2F4F7" : "#FFFFFF"}
            text={importLoading ? "Importing..." : "Import"}
            color="#424242"
            icon={<Uploadbutton />}
            gap="10px"
            padding="10px 15px"
            borderRadius="20px"
            border="1px solid #D0D5DD"
            boxShadow="none"
            onClick={
              importLoading
                ? undefined
                : () => {
                    setImportError("");
                    importInputRef.current?.click();
                  }
            }
          />
          <GloablButton
            className="productCatalog-toolbar-action-button"
            width="159px"
            height="40px"
            backgroundColor="#FFFFFF"
            text="Export/Print"
            color="#424242"
            icon={<ExportAndPrintIcon />}
            gap="10px"
            padding="10px 15px"
            borderRadius="20px"
            border="1px solid #D0D5DD"
            boxShadow="none"
            onClick={() => setActiveModalKey("export")}
          />
        </div>
      </div>

      {importError ? (
        <div className="productCatalog-import-error">{importError}</div>
      ) : null}

      <div className="productCatalog-card-grid-section">
        {catalogLoading ? (
          <div className="productCatalog-feedback-state">
            Loading product catalog...
          </div>
        ) : null}

        {!catalogLoading && catalogError ? (
          <div className="productCatalog-feedback-state">{catalogError}</div>
        ) : null}

        {!catalogLoading && !catalogError && catalogItems.length === 0 ? (
          <div className="productCatalog-feedback-state">
            No products found.
          </div>
        ) : null}

        {!catalogLoading && !catalogError
          ? catalogItems.map((productCard) => (
              <div key={productCard.id} className="productCatalog-card-item">
                <div className="productCatalog-card-image-wrapper">
                  {productCard.imageSrc ? (
                    <img
                      src={productCard.imageSrc}
                      alt={productCard.productName}
                      className="productCatalog-card-image"
                    />
                  ) : (
                    <div className="productCatalog-card-no-image">No image</div>
                  )}
                  {/* <div
                    className={`productCatalog-card-status-badge productCatalog-card-status-badge-${productCard.badgeTone}`}
                  >
                    {productCard.badgeLabel}
                  </div> */}
                </div>

                <div className="productCatalog-card-content-section">
                  <div className="productCatalog-card-product-name">
                    {productCard.productName}
                  </div>
                  <div className="productCatalog-card-product-sku">
                    {productCard.sku}
                  </div>

                  <div className="productCatalog-card-category-row">
                    <div className="productCatalog-card-meta-icon-shell">
                      <CubeIcon />
                    </div>
                    <div className="productCatalog-card-category-text">
                      {productCard.category}
                    </div>
                  </div>

                  <div className="productCatalog-card-product-price">
                    {productCard.price}
                  </div>

                  <div className="productCatalog-card-meta-row">
                    <div className="productCatalog-card-meta-item">
                      <div className="productCatalog-card-meta-icon-shell">
                        <ShareIcon />
                      </div>
                      <div className="productCatalog-card-meta-text">
                        {productCard.variants}
                      </div>
                    </div>

                    <div className="productCatalog-card-meta-item productCatalog-card-meta-item-stock">
                      <div className="productCatalog-card-meta-icon-shell">
                        <OutOfStockIcon />
                      </div>
                      <div
                        className={`productCatalog-card-stock-text productCatalog-card-stock-text-${productCard.stockTone}`}
                      >
                        {productCard.stockStatus}
                      </div>
                    </div>
                  </div>

                  <div className="productCatalog-card-button-row">
                    <div className="productCatalog-card-button-item">
                      <GloablButton
                        width="137px"
                        height="32px"
                        backgroundColor="rgba(0, 0, 0, 0.0001)"
                        text="View Details"
                        color="#424242"
                        fontWeight={600}
                        fontSize="14px"
                        lineHeight="16px"
                        textAlign="center"
                        border="1px solid #E2E8F0"
                        borderRadius="50px"
                        padding="6px 12px"
                        boxShadow="0px 1px 2px rgba(0, 0, 0, 0.05), 0px 0px 0px rgba(0, 0, 0, 0.0001), 0px 0px 0px rgba(0, 0, 0, 0.0001)"
                        onClick={() => {
                          const detailsNavigation =
                            createProductDetailsNavigation(productCard);

                          navigateWithTableState(
                            detailsNavigation.path,
                            currentTableState,
                            {
                              state: detailsNavigation.state,
                            },
                          );
                        }}
                      />
                    </div>

                    <div className="productCatalog-card-button-item">
                      <GloablButton
                        width="135px"
                        height="32px"
                        backgroundColor="#4072C8"
                        text="Edit"
                        color="#FFFFFF"
                        fontWeight={600}
                        fontSize="14px"
                        lineHeight="16px"
                        textAlign="center"
                        borderRadius="999px"
                        padding="6px 12px"
                        boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.45)"
                        onClick={() => {
                          const detailsNavigation =
                            createProductDetailsNavigation(productCard, {
                              isEditing: true,
                            });

                          navigateWithTableState(
                            detailsNavigation.path,
                            currentTableState,
                            {
                              state: detailsNavigation.state,
                            },
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))
          : null}
      </div>

      {!catalogLoading && !catalogError && serverTotalPages > 1 ? (
        <div className="productCatalog-toolbar-section">
          <div className="productCatalog-toolbar-result-count">
            Page {currentPage} of {serverTotalPages}
          </div>
          <div className="productCatalog-toolbar-actions-row productCatalog-pagination-controls">
            <form
              className="productCatalog-pagination-page-input-form"
              onSubmit={(e) => {
                e.preventDefault();
                goToTypedPage();
              }}
            >
              <input
                type="number"
                min={DEFAULT_PAGE}
                max={serverTotalPages}
                className="productCatalog-pagination-page-input"
                value={
                  isPageInputFocused ? pageInputValue : String(currentPage)
                }
                onFocus={(e) => {
                  setIsPageInputFocused(true);
                  setPageInputValue(String(currentPage));
                  e.target.select();
                }}
                onChange={(e) => setPageInputValue(e.target.value)}
                onBlur={() => {
                  if (!goToTypedPage()) {
                    setPageInputValue("");
                    setIsPageInputFocused(false);
                  }
                }}
                aria-label="Go to page"
              />
            </form>

            <div className="productCatalog-pagination-bar">
              <button
                type="button"
                className={`productCatalog-pagination-arrow ${
                  canGoToPreviousPage ? "" : "is-disabled"
                }`}
                onClick={() => {
                  if (canGoToPreviousPage) {
                    setCurrentPage((prev) => prev - 1);
                    setPageInputValue("");
                    setIsPageInputFocused(false);
                  }
                }}
                disabled={!canGoToPreviousPage}
                aria-label="Previous page"
              >
                <ArrowBackIcon />
              </button>

              <div className="productCatalog-pagination-pages">
                {getCatalogPages().map((item) =>
                  typeof item === "string" ? (
                    <div
                      className="productCatalog-pagination-ellipsis"
                      key={item}
                    >
                      ...
                    </div>
                  ) : (
                    <button
                      type="button"
                      key={item}
                      className={`productCatalog-pagination-page ${
                        currentPage === item ? "is-active" : ""
                      }`}
                      onClick={() => {
                        setCurrentPage(item);
                        setPageInputValue("");
                        setIsPageInputFocused(false);
                      }}
                    >
                      {item}
                    </button>
                  ),
                )}
              </div>

              <button
                type="button"
                className={`productCatalog-pagination-arrow ${
                  canGoToNextPage ? "" : "is-disabled"
                }`}
                onClick={() => {
                  if (canGoToNextPage) {
                    setCurrentPage((prev) => prev + 1);
                    setPageInputValue("");
                    setIsPageInputFocused(false);
                  }
                }}
                disabled={!canGoToNextPage}
                aria-label="Next page"
              >
                <ArrowFront />
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* {activeModalKey === "sort" ? (
        <div
          className="productCatalog-sort-panel"
          ref={sortPanelRef}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="productCatalog-sort-panel-title">Sort</div>

          {sortSections.map((section) => (
            <div key={section.key} className="productCatalog-sort-section">
              <div className="productCatalog-sort-section-head">
                <div className="productCatalog-sort-section-title">
                  {section.title}
                </div>
                <button
                  type="button"
                  className="productCatalog-sort-clear-button"
                  onClick={() =>
                    setSelectedSortOptions((prev) => ({
                      ...prev,
                      [section.key]: "",
                    }))
                  }
                >
                  Clear
                </button>
              </div>

              <div className="productCatalog-sort-option-list">
                {section.options.map((option) => (
                  <label
                    key={option}
                    className="productCatalog-sort-option-item"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSortOptions[section.key] === option}
                      onChange={() =>
                        setSelectedSortOptions((prev) => ({
                          ...prev,
                          [section.key]:
                            prev[section.key] === option ? "" : option,
                        }))
                      }
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div className="productCatalog-sort-footer">
            <GloablButton
              width="110px"
              height="36px"
              backgroundColor="#FFFFFF"
              text="Clear All"
              color="#667085"
              fontWeight={600}
              fontSize="12px"
              lineHeight="16px"
              border="2px solid #667085"
              borderRadius="50px"
              padding="10px 15px"
              onClick={clearSortSelections}
            />
            <GloablButton
              width="103px"
              height="32px"
              backgroundColor="#4072C8"
              text="Apply All"
              color="#FFFFFF"
              fontWeight={600}
              fontSize="12px"
              lineHeight="16px"
              borderRadius="50px"
              padding="8px 12px"
              boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.45)"
              onClick={applySelectedSort}
            />
          </div>
        </div>
      ) : null} */}

      {/* {activeModalKey === "filter" ? (
        <div
          className="productCatalog-filter-panel"
          ref={filterPanelRef}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="productCatalog-filter-panel-title">Filter</div>

          <div className="productCatalog-filter-section">
            <div className="productCatalog-filter-section-head">
              <div className="productCatalog-filter-section-title">Search</div>
              <button
                type="button"
                className="productCatalog-filter-clear-button"
                onClick={() => setFilterSearch("")}
              >
                Clear
              </button>
            </div>

            <div className="productCatalog-filter-search-shell">
              <input
                className="productCatalog-filter-search-input"
                value={filterSearch}
                onChange={(event) => setFilterSearch(event.target.value)}
                placeholder="Search by product name, SKU, Category..."
              />
            </div>
          </div>

          {filterSections.map((section) => {
            const visibleOptions = showAllFilterOptions[section.key]
              ? section.options
              : section.options.slice(0, 3);

            return (
              <div key={section.key} className="productCatalog-filter-section">
                <div className="productCatalog-filter-section-head">
                  <div className="productCatalog-filter-section-title">
                    {section.title}
                  </div>
                  <button
                    type="button"
                    className="productCatalog-filter-clear-button"
                    onClick={() =>
                      setSelectedFilterOptions((prev) => ({
                        ...prev,
                        [section.key]: [],
                      }))
                    }
                  >
                    Clear
                  </button>
                </div>

                <div className="productCatalog-filter-option-list">
                  {visibleOptions.map((option) => {
                    const isChecked =
                      selectedFilterOptions[section.key].includes(option);

                    return (
                      <label
                        key={option}
                        className="productCatalog-filter-option-item"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() =>
                            setSelectedFilterOptions((prev) => ({
                              ...prev,
                              [section.key]: isChecked
                                ? prev[section.key].filter(
                                    (item) => item !== option,
                                  )
                                : [...prev[section.key], option],
                            }))
                          }
                        />
                        <span>{option}</span>
                      </label>
                    );
                  })}
                </div>

                {section.options.length > 3 ? (
                  <button
                    type="button"
                    className="productCatalog-filter-show-more-button"
                    onClick={() =>
                      setShowAllFilterOptions((prev) => ({
                        ...prev,
                        [section.key]: !prev[section.key],
                      }))
                    }
                  >
                    {showAllFilterOptions[section.key]
                      ? "Show less"
                      : "Show more"}
                  </button>
                ) : null}
              </div>
            );
          })}

          <div className="productCatalog-filter-footer">
            <GloablButton
              width="110px"
              height="36px"
              backgroundColor="#FFFFFF"
              text="Clear All"
              color="#667085"
              fontWeight={600}
              fontSize="12px"
              lineHeight="16px"
              border="2px solid #667085"
              borderRadius="50px"
              padding="10px 15px"
              onClick={clearAllFilterSelections}
            />
            <GloablButton
              width="103px"
              height="32px"
              backgroundColor="#4072C8"
              text="Apply All"
              color="#FFFFFF"
              fontWeight={600}
              fontSize="12px"
              lineHeight="16px"
              borderRadius="50px"
              padding="8px 12px"
              boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.45)"
              onClick={applySelectedFilters}
            />
          </div>
        </div>
      ) : null} */}

      {activeModalCopy &&
      activeModalKey !== "sort" &&
      activeModalKey !== "filter" ? (
        <div
          className="productCatalog-modal-overlay"
          onClick={() => setActiveModalKey("")}
        >
          <div
            className="productCatalog-modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="productCatalog-modal-close-button"
              onClick={() => setActiveModalKey("")}
            >
              <CloseIcon />
            </button>

            <div className="productCatalog-modal-title">
              {activeModalCopy.title}
            </div>
            <div className="productCatalog-modal-description">
              {activeModalCopy.description}
            </div>

            {activeModalKey === "export" ? (
              <>
                <div className="productCatalog-modal-option-list">
                  <div className="productCatalog-modal-option-item productCatalog-modal-option-item-active">
                    <div className="productCatalog-modal-option-copy">
                      <span className="productCatalog-modal-option-title">
                        Export as Excel
                      </span>
                      <span className="productCatalog-modal-option-subtitle">
                        {selectedExportColumns.length} columns selected
                      </span>
                    </div>
                  </div>
                </div>

                <div className="productCatalog-export-search-section">
                  <div className="productCatalog-export-column-header">
                    <div className="productCatalog-export-column-title">
                      Search
                    </div>
                    <button
                      type="button"
                      className="productCatalog-export-link-button"
                      onClick={() => {
                        setExportSearch("");
                        setExportError("");
                      }}
                    >
                      Clear
                    </button>
                  </div>
                  <div className="productCatalog-export-search-shell">
                    <input
                      className="productCatalog-export-search-input"
                      value={exportSearch}
                      onChange={(event) => {
                        setExportSearch(event.target.value);
                        setExportError("");
                      }}
                      placeholder="Search by product name, SKU, Category..."
                    />
                  </div>
                </div>

                <div className="productCatalog-export-sort-section">
                  <div className="productCatalog-export-column-title">Sort</div>
                  <div className="productCatalog-export-sort-grid">
                    <div className="productCatalog-export-select-field">
                      <span>Sort by</span>
                      <CustomDropdown
                        value={exportSortBy}
                        onChange={(value) => {
                          setExportSortBy(value);
                          setExportError("");
                        }}
                        options={EXPORT_SORT_OPTIONS}
                        placeholder="Sort by"
                        rightIcon={<ArrowDownIcon />}
                        className="productCatalog-export-custom-dropdown"
                        openClassName="productCatalog-export-custom-dropdown-open"
                        menuClassName="productCatalog-export-custom-dropdown-menu"
                        optionClassName="productCatalog-export-custom-dropdown-option"
                      />
                    </div>

                    <div className="productCatalog-export-direction-group">
                      <div className="productCatalog-export-direction-label">
                        Direction
                      </div>
                      <div className="productCatalog-export-direction-options">
                        <label className="productCatalog-export-direction-item">
                          <input
                            type="radio"
                            name="productCatalogExportDirection"
                            value="ASC"
                            checked={exportSortDirection === "ASC"}
                            onChange={(event) => {
                              setExportSortDirection(event.target.value);
                              setExportError("");
                            }}
                          />
                          <span>Ascending</span>
                        </label>
                        <label className="productCatalog-export-direction-item">
                          <input
                            type="radio"
                            name="productCatalogExportDirection"
                            value="DESC"
                            checked={exportSortDirection === "DESC"}
                            onChange={(event) => {
                              setExportSortDirection(event.target.value);
                              setExportError("");
                            }}
                          />
                          <span>Descending</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="productCatalog-export-column-header">
                  <div className="productCatalog-export-column-title">
                    Columns
                  </div>
                  <div className="productCatalog-export-column-actions">
                    <button
                      type="button"
                      className="productCatalog-export-link-button"
                      onClick={() =>
                        setSelectedExportColumns(DEFAULT_EXPORT_COLUMNS)
                      }
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="productCatalog-export-link-button"
                      onClick={() =>
                        setSelectedExportColumns(REQUIRED_EXPORT_COLUMNS)
                      }
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="productCatalog-export-column-grid">
                  {EXPORT_COLUMNS.map((column) => {
                    const isRequired = REQUIRED_EXPORT_COLUMNS.includes(
                      column.key,
                    );
                    const isChecked =
                      isRequired || selectedExportColumns.includes(column.key);

                    return (
                      <label
                        key={column.key}
                        className={`productCatalog-export-column-item${
                          isRequired
                            ? " productCatalog-export-column-item-locked"
                            : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isRequired}
                          onChange={() => toggleExportColumn(column.key)}
                        />
                        <span>{column.label}</span>
                      </label>
                    );
                  })}
                </div>

                {exportError ? (
                  <div className="productCatalog-export-error">
                    {exportError}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="productCatalog-modal-option-list">
                {activeModalCopy.items.map((item) => (
                  <div key={item} className="productCatalog-modal-option-item">
                    {item}
                  </div>
                ))}
              </div>
            )}

            <div className="productCatalog-modal-footer">
              <GloablButton
                width="120px"
                height="40px"
                backgroundColor="#FFFFFF"
                text="Cancel"
                color="#424242"
                border="1px solid #D0D5DD"
                borderRadius="999px"
                padding="8px 14px"
                onClick={() => {
                  setExportError("");
                  setActiveModalKey("");
                }}
              />
              <GloablButton
                width="120px"
                height="40px"
                backgroundColor={
                  exportLoading || selectedExportColumns.length === 0
                    ? "#98A2B3"
                    : "#4072C8"
                }
                text={exportLoading ? "Exporting..." : "Export"}
                color="#FFFFFF"
                borderRadius="999px"
                padding="8px 14px"
                boxShadow="inset 0px -1px 3px rgba(0, 18, 71, 0.45)"
                onClick={
                  exportLoading || selectedExportColumns.length === 0
                    ? undefined
                    : handleExportCatalog
                }
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ProductCatalog;
